// components/Settings.js
import React, { useState } from "react";
import { useAuth } from "components/ui/navigationGuard";
import AdminNavigation from "layouts/adminNavigation";
import Button from "components/ui/button";
import Input from "components/ui/input";
import Notification from "components/ui/notification";
import useSettingsLogic from "hooks/useSettings";

const Settings = () => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Use the custom hook to get all the state and functions
  const {
    loading,
    settings,
    editing,
    hasUnsavedChanges,
    notification,
    formData,
    setEditing,
    showNotification,
    closeNotification,
    fetchSettings,
    handlePeriodChange,
    handleHoursChange,
    handleHalfDayThresholdChange,
    initializeWorkHoursSetting,
    handleSaveSettings,
    handleCancelEdit
  } = useSettingsLogic();
  
  const handleToggleCollapse = () => setIsCollapsed(!isCollapsed);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation
        user={user}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <main
        className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? "lg:pl-16" : "lg:pl-64"
          }`}
      >
        <div className="container mx-auto mt-10">
          <h2 className="text-2xl font-bold mb-6">
            ⚙️ System Settings
          </h2>

          {!settings.day && !settings.week && !settings.halfDayThreshold && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-yellow-700 mb-3">
                Work hours settings are not initialized. Please initialize them to configure work hour limits.
              </p>
              <Button
                onClick={initializeWorkHoursSetting}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                Initialize Work Hours Settings
              </Button>
            </div>
          )}

          <div className="border rounded-lg p-6 mb-6 bg-white shadow-sm">
            <h3 className="font-semibold mb-4">Work Hours Configuration</h3>
            
            <div className="space-y-8">
              {/* Period Selection with Radio Buttons */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium text-gray-800 mb-4">Select Hours Limit Type</h4>
                
                <div className="flex space-x-6">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="period"
                      className="form-radio h-5 w-5 text-primary"
                      checked={formData.activePeriod === 'day'}
                      onChange={() => handlePeriodChange('day')}
                      disabled={!editing}
                    />
                    <span className="ml-2 text-gray-700">Daily Hours Limit</span>
                  </label>
                  
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="period"
                      className="form-radio h-5 w-5 text-primary"
                      checked={formData.activePeriod === 'week'}
                      onChange={() => handlePeriodChange('week')}
                      disabled={!editing}
                    />
                    <span className="ml-2 text-gray-700">Weekly Hours Limit</span>
                  </label>
                </div>
              </div>

              {/* Hours Configuration */}
              <div className="border rounded-lg p-4 bg-green-50">
                <h4 className="font-medium text-gray-800 mb-4">
                  {formData.activePeriod === 'day' ? 'Daily Hours Configuration' : 'Weekly Hours Configuration'}
                </h4>
                
                <div className="max-w-md">
                  <label className="block text-sm font-medium mb-1">
                    {formData.activePeriod === 'day' ? 'Hours Per Day' : 'Hours Per Week'}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max={formData.activePeriod === 'day' ? "24" : "168"}
                    value={formData.activePeriod === 'day' ? formData.day.hours : formData.week.hours}
                    onChange={(e) => handleHoursChange(formData.activePeriod, e.target.value)}
                    disabled={!editing}
                    placeholder={`Enter hours per ${formData.activePeriod === 'day' ? 'day' : 'week'}`}
                  />
                </div>
              </div>

              {/* Half-Day Threshold Section */}
              <div className="border rounded-lg p-4 bg-purple-50">
                <h4 className="font-medium text-gray-800 mb-4">Half-Day Threshold</h4>
                
                <div className="border rounded-lg p-4 bg-white">
                  <div className="mb-3">
                    <h5 className="font-medium text-gray-700">Minimum Hours to Avoid Half-Day</h5>
                    <p className="text-sm text-gray-500">Employees working less than this threshold will be marked as half-day absent.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Threshold (hours)
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      step="0.5"
                      value={formData.halfDayThreshold.hours}
                      onChange={(e) => handleHalfDayThresholdChange(e.target.value)}
                      disabled={!editing}
                      placeholder="Enter threshold in hours"
                    />
                  </div>
                </div>
              </div>

              {/* Current Settings Display */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium">Current settings:</span>
                  {formData.activePeriod === 'day' ? (
                    <span> Daily limit: {formData.day.hours} hours/day</span>
                  ) : (
                    <span> Weekly limit: {formData.week.hours} hours/week</span>
                  )}
                  <span> | Half-day threshold: {formData.halfDayThreshold.hours}h</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                {!editing ? (
                  <Button
                    onClick={() => setEditing(true)}
                    className="px-4 py-2"
                    disabled={!settings.day && !settings.week && !settings.halfDayThreshold}
                  >
                    Edit Settings
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handleSaveSettings}
                      disabled={!hasUnsavedChanges}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700"
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-500 hover:bg-gray-600"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Notification Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </div>
  );
};

export default Settings;